<template>
  <div class="synthese__container">
    <SyntheseSection
      title="L'employeur"
      :items="employerDetails"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-if="estDossier"
      title="Le salarié(e)"
      :items="salarieDetails"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-if="estDossier"
      title="Le tuteur entreprise travail temporaire"
      :items="tuteurEntrepriseDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      title="Formation"
      :items="formationDetail"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-if="bilanPositionnement"
      :title="TypeModuleLabel.BilanPositionnement"
      :items="bilanPositionnement"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-for="(formationExterne, index) in formationsExterne"
      :key="index"
      :title="`Formation ${TypeModuleLabel.Externe} #${index + 1}`"
      :items="formationExterne"
    />
    <SyntheseSection
      v-if="bilanEvaluation"
      :title="TypeModuleLabel.BilanEvaluation"
      :items="bilanEvaluation"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-if="estDossier && model?.sousDispositif === 'CIPI'"
      title="Mission"
      :items="mission"
      class="col-14 col-sm-36"
    />
    <SyntheseSection
      v-if="estDossier"
      title="Chiffrage (montants HT)"
      :items="chiffrage"
      class="col-14 col-sm-36"
    />


  </div>

  <ListeMontantsDemandesSection  v-if="estReservation"/>

  <FormContainer>
    <template #content>
      <WrapperCheckbox
        id="field_102"
        v-model="attestationSurHonneurModel"
        :required="meta.attestationSurHonneur?.$status === PropertyStatus.Required"
        :alertLabel="meta.attestationSurHonneur?.$messageErreurDirty!"
        label="J’atteste sur l’honneur l’exactitude des données saisies."
        :readonly
        class="col-36"
      />
      <WrapperCheckbox
        id="field_103"
        v-model="acceptationCgModel"
        :required="meta.acceptationCg?.$status === PropertyStatus.Required"
        :alertLabel="meta.acceptationCg?.$messageErreurDirty!"
        :readonly
        class="col-36"
      >
        <BasicText style="flex: 1">
          J'accepte les Conditions Générales (CG) d'Akto et les conditions de prise en charge, et je confirme avoir pris
          connaissance de celles-ci via le lien suivant : «
          <BasicLink
            label="Conditions générales - Akto"
            @link-click="redirectExterne('https://www.akto.fr/conditions-generales')"
          />
        </BasicText>
        »
      </WrapperCheckbox>
      <div class="form__bloc">
        <WrapperInput
          id="field_104"
          v-model="lieuSignatureModel"
          label="Fait à"
          :required="meta.lieuSignature?.$status === PropertyStatus.Required"
          :alertLabel="meta.lieuSignature?.$messageErreurDirty!"
          class="col-12 col-md-14 col-sm-36"
          deletable
          hint="50 caractères maximum"
          :maxlength="50"
          :readonly
        />

        <WrapperInputDate
          id="field_dateSignature"
          v-model="dateSignatureModel"
          label="Fait le"
          :required="meta.dateSignature?.$status === PropertyStatus.Required"
          :alertLabel="meta.dateSignature?.$messageErreurDirty!"
          class="col-12 col-md-14 col-sm-36"
          deletable
          :readonly
        />
        <WrapperInput
          id="field_105"
          v-model="commentaireModel"
          label="Commentaires (infos complémentaires...)"
          :required="meta.commentaire?.$status === PropertyStatus.Required"
          :alertLabel="meta.commentaire?.$messageErreurDirty!"
          class="col-36"
          deletable
          hint="256 caractères maximum"
          :maxlength="256"
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import type { ModuleDTO } from '@/features/api/extranet/dto/Demande/CXPI'
import type { LovDTO } from '@/features/api/extranet/dto/Lov'
import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
import { TypeModuleLabel, TypeQualificationLibelle } from '@/features/domaine/types/opco/Demande/CXPI'
import { DEMANDE_STORE, FormContainer, SyntheseSection } from '@/features/interface/demande/shared'
import {
  dateFormatter,
  formatNumber,
  isDate,
  isNotNullUndefinedOrEmptyString,
  isNullOrUndefined,
} from '@/features/shared/tools'
import { redirectExterne } from '@/features/shared/tools/url'
import { storeToRefs } from 'pinia'
import { computed, inject } from 'vue'
import ListeMontantsDemandesSection from './sections/ListeMontantsDemandesSection.vue'

  const store = inject(DEMANDE_STORE)!.getStore('MODEL_CXPI')

  const { model, lov, setupFieldWithKey } = store
  const { readonly, meta, estDossier, estReservation} = storeToRefs(store)

  const lieuSignatureModel = setupFieldWithKey<string>('lieuSignature', true)
  const commentaireModel = setupFieldWithKey<string>('commentaire', true)
  const attestationSurHonneurModel = setupFieldWithKey<boolean>('attestationSurHonneur')
  const acceptationCgModel = setupFieldWithKey<boolean>('acceptationCg')
  const dateSignatureModel = setupFieldWithKey<string>('dateSignature')

  const getLovIntitule = <T extends string | number>(lovDTO: LovDTO<T>, code?: T | null) => {
    if (isNullOrUndefined(code)) return ''
    return lovDTO.find((item) => item.code === code)?.intitule ?? ''
  }

  const getDynamicLovIntitule = <T extends string | number>(lovDTO: LovDTO<T>, code: T | null | undefined) =>
    getLovIntitule(lovDTO, code)?.split('-')?.at(1) ?? ''

  const getDateIntitule = (value: any) => (isDate(value) ? dateFormatter(value) : '')

  const employerDetails = computed(() => ({
    'Nom et prénom ou dénomination': model?.employeur?.etablissement?.denomination,
    Siret: model?.employeur?.etablissement?.siret?.valeur,
    'IDCC applicable': model?.employeur?.etablissement?.idcc?.code,
    ...(estDossier.value
    ? {  Email: model?.employeur?.contact?.email }
    : {}),
    'N° et Voie': model?.employeur?.etablissement?.adresse?.voie,
    Complément: model?.employeur?.etablissement?.adresse?.complement,
    'CP Ville': `${model?.employeur?.etablissement?.adresse?.codePostal} ${model?.employeur?.etablissement?.adresse?.commune} `,
  }))


  const salarieDetails = computed(() => ({
    'Nom de naissance': model?.salarie?.nomNaissance,
    "Nom d'usage": model?.salarie?.nom,
    Prénom: model?.salarie?.prenom,
    'Date de naissance': getDateIntitule(model?.salarie?.dateNaissance),
    Email: model?.salarie?.email ?? '',
  }))

  const tuteurEntrepriseDetail = computed(() => {
    const nomComplet =
      [model?.salarie?.tuteur?.prenom, model?.salarie?.tuteur?.nom]
        .filter(isNotNullUndefinedOrEmptyString)
        .join(' ')
        .trim() || '-'

    return {
      'Nom complet': nomComplet,
    }
  })

  const formationDetail = computed(() => ({
    'Intitule de la formation': model?.formation?.intitule,
    'Type de qualification visée': TypeQualificationLibelle[model!.formation!.typeQualification!],
    'RNCP / Formacode':
      model?.formation?.rncp != null
        ? model!.formation!.rncp!.code! + ' - ' + model!.formation!.rncp!.intitule!
        : model?.formation?.formacode != null
          ? model!.formation!.formacode!.code! + ' - ' + model!.formation!.formacode!.intitule!
          : '-',
    ...(estDossier.value
      ? {  'Diplôme ou titre visé': getDynamicLovIntitule(lov.Diplome, model?.formation?.diplomeOuTitreVise) }
      : {}),

  }))

  function mapModule(module: ModuleDTO): Record<string, string> {
    const isExterne = module?.typeModule === 'Externe'

    return {
      'Organisme de formation': module?.etablissementPrestataire?.denomination ?? '',
      'Date de début': getDateIntitule(module?.dateDebut),
      [isExterne ? 'Date de fin de formation' : 'Date de fin']: getDateIntitule(module?.dateFin),
      [isExterne ? 'Durée de la formation' : 'Durée du bilan']: module?.duree ? `${module?.duree}h` : '',
      ...(estDossier.value
        ? {  'Coût pédagogique': `${formatNumber(
          module?.fraisPedagogiques
            ?.filter((x) => x.nature === 'Pedagogique')
            .reduce((acc, frais) => acc + (frais.montant ?? 0), 0),
          { decimal: 2, separateur: true },
        )} €` }
        : {}),
      ...(estDossier.value
        ? {  'Autres types de coûts': `${formatNumber(
        (module?.fraisPedagogiques
          ?.filter((x) => x.nature !== 'Pedagogique')
          .reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0) +
          (module?.fraisAnnexesSalarie?.reduce((acc, frais) => acc + (frais.montant ?? 0), 0) ?? 0),
        { decimal: 2, separateur: true },
      )} €` }
        : {}),

    }
  }

  const bilanPositionnement = computed(() => {
    const bilan = model?.formation?.modules?.find((m) => m.typeModule === 'BilanPositionnement')
    if (!bilan) return null
    return mapModule(bilan)
  })

  const bilanEvaluation = computed(() => {
    const bilan = model?.formation?.modules?.find((m) => m.typeModule === 'BilanEvaluation')
    if (!bilan) return null
    return mapModule(bilan)
  })

  const formationsExterne = computed(() => {
    return (
      model?.formation?.modules?.filter((module) => module.typeModule === 'Externe')?.map((m) => mapModule(m)) ?? []
    )
  })

  const mission = computed(() => ({
    'Date de début': getDateIntitule(model?.mission?.dateDebut),
    'Date de fin': getDateIntitule(model?.mission?.dateFin),
    Durée: isNotNullUndefinedOrEmptyString(model?.mission?.duree) ? `${model?.mission?.duree}h` : '',
    ...(model?.sousDispositif === 'CDPI'
      ? {  'Poste occupé': model?.mission?.posteOccupe }
      : {}),
  }))

  const chiffrage = computed(() => {
    const coutPedagogique = model?.formation?.modules
      ? model.formation.modules
          .flatMap((module) => module?.fraisPedagogiques ?? [])
          .reduce((acc, frais) => acc + (frais.montant ?? 0), 0)
      : 0

    // const remuneration = model?.formation?.remuneration ?? 0
    // const dureeFormation = model?.formation?.modules?.reduce((sum, mod) => sum + (mod.duree ?? 0), 0) ?? 0

    const remuneration = computed(() => {
      const dureeModule = model?.formation?.modules?.reduce((acc, mod) => acc + (mod.duree ?? 0), 0) ?? 0
      const salaire = model?.contrat?.salaireHoraireFormation ?? 0
      return dureeModule * salaire
    })

    const fraisAnnexes = model?.formation?.modules
      ? model.formation.modules
          .flatMap((module) => module?.fraisAnnexesSalarie ?? [])
          .reduce((acc, frais) => acc + (frais.montant ?? 0), 0)
      : 0
    const total = coutPedagogique + remuneration.value + fraisAnnexes
    const remunerationLibelle = model?.sousDispositif === 'CDPI' ? 'Rémunération' : 'Rémunération (hors mission)'

    return {
      'Frais de formation': `${formatNumber(coutPedagogique, { decimal: 2, separateur: true })} €`,
      [remunerationLibelle] : `${formatNumber(remuneration.value, { decimal: 2, separateur: true })} €`,
      'Frais annexes': `${formatNumber(fraisAnnexes, { decimal: 2, separateur: true })} €`,
      Total: `${formatNumber(total, { decimal: 2, separateur: true })} €`,
    }
  })
</script>
<style scoped lang="less">
  .synthese__container {
    width: 90%;
    align-self: center;
    margin: @spacing-20 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    gap: 20px;

    > div {
      width: calc(45% - 10px);
      border-radius: @border-radius-l;

      @media (max-width: @breakpoint-tablet) {
        width: 100%;
      }
    }

    @container (max-width: @breakpoint-mobile) {
      width: 100%;
    }
  }

  .synthese {
    &__bloc {
      margin: @spacing-20 0;
      display: grid;
      grid-template-columns: repeat(36, 1fr);
      grid-row-gap: @spacing-20;
      grid-column-gap: 2%;
    }
  }
</style>
